import {
  Grid,
  IconButton,
  InputAdornment,
  makeStyles,
  TextField,
  Typography,
  withStyles,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { useState } from "react";
import { FaCaretUp, FaCaretDown } from "react-icons/fa";

import { COLORS } from "../../theme/colors";

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  multipleTextFieldRoot: {
    width: "100%",
  },
  width80: {
    width: "80%",
  },
  input: {
    padding: theme.spacing(1.5),
  },
  suffix: {
    marginLeft: theme.spacing(1.5),
  },
  prefix: {
    marginRight: theme.spacing(1.5),
  },
  eyePassword: {
    paddingRight: 0,
  },
  understoreInput: {
    width: (props) => props.width,
    backgroundColor: "transparent",
    border: "none",
    borderBottom: `1px solid ${COLORS.darks[0][50]}`,
    "&:focus-visible": {
      outline: "none",
    },
    "&::placeholder": {
      fontStyle: "italic",
      fontSize: 14,
      color: COLORS.darks[0][50],
    },
  },
}));

export const TextInput = ({
  containerStyle,
  type,
  label,
  placeHolder,
  prefix,
  suffix,
  value,
  onChange,
  onFocus,
  fullWidth,
  error,
  helperText,
  startAdornment,
  rootStyle,
  endAdornment,
  endAdornmentIcon,
  inputRootStyle,
  id,
  variant,
  labelRoot,
  disabled,
  style,
  onBlur,
  justifyContent = "center",
}) => {
  const classes = useStyles();
  const [showPassword, setShowPassword] = useState(false);
  return (
    <Grid id={id} className={containerStyle || classes.root} container direction="column">
      {label && <Typography className={labelRoot}>{label}</Typography>}
      <Grid container alignItems="center" justifyContent={justifyContent}>
        {prefix && <Typography className={classes.prefix}>{prefix}</Typography>}
        <TextField
          type={type === "password" && showPassword ? "text" : type}
          variant={variant || "outlined"}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onFocus={onFocus}
          placeholder={placeHolder}
          fullWidth={fullWidth}
          style={style}
          onBlur={onBlur}
          disabled={disabled}
          InputProps={{
            classes: {
              input: inputRootStyle || classes.input,
            },
            ...{ startAdornment: startAdornment ? startAdornment : null },
            endAdornment:
              type === "password" ? (
                <InputAdornment className={classes.eyePassword} position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    onMouseDown={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ) : (
                endAdornment || endAdornmentIcon || null
              ),
          }}
          error={!!error}
          helperText={helperText || typeof error === "string" ? helperText || error : null}
          classes={{
            root: suffix || prefix ? classes.width80 : rootStyle,
          }}
        />
        {suffix && <Typography className={classes.suffix}>{suffix}</Typography>}
      </Grid>
    </Grid>
  );
};

const transparentInputStyle = (width, textAlign, type, fontSize) => ({
  border: "none",
  outline: "none",
  width: width || "100%",
  backgroundColor: "transparent",
  textAlign: textAlign ? textAlign : type === "number" ? "right" : null,
  padding: 0,
  fontSize: fontSize,
  color: COLORS.darks[0][100],
});

export const TransparentTextInput = ({
  value,
  onChange,
  type,
  align,
  textAlign,
  width,
  fontSize,
  min,
  max,
  disabled,
}) => {
  return (
    <input
      min={min}
      max={max}
      align={align}
      type={type}
      disabled={disabled}
      style={transparentInputStyle(width, textAlign, type, fontSize)}
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
    />
  );
};

export const TableTransparentTextInput = ({
  defaultValue,
  onBlur,
  type,
  align,
  textAlign,
  width,
  fontSize,
  min,
  max,
  disabled,
}) => {
  return (
    <input
      defaultValue={defaultValue}
      min={min}
      max={max}
      align={align}
      type={type}
      disabled={disabled}
      style={transparentInputStyle(width, textAlign, type, fontSize)}
      onBlur={(event) => onBlur(event.target.value)}
    />
  );
};

export const UnderscoreTextInput = ({ width, value, onChange, placeHolder }) => {
  const classes = useStyles({ width });

  return (
    <input
      className={classes.understoreInput}
      value={value}
      placeholder={placeHolder}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

export const TextAreaInput = ({ ref, value, onChange }) => {
  const classes = useStyles();

  return (
    <TextField
      ref={ref}
      maxRows={20}
      minRows={10}
      multiline
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
      classes={{ root: classes.multipleTextFieldRoot }}
      variant="outlined"
    />
  );
};

const SearchTextFieldInput = withStyles({
  root: {
    width: 500,
    marginRight: 10,
    "& .MuiInput-underline": {
      borderBottom: `1px solid ${COLORS.darks[0][25]}`,
    },
    "& .MuiInput-underline:after": {
      borderBottom: "none",
    },
  },
})(TextField);

export const SearchTextField = ({ id }) => {
  return <SearchTextFieldInput id={id} />;
};

export const ArrowNumberInput = ({
  inputWidth,
  value = "0",
  onChange,
  min = 0,
  max = 100,
  step = 1,
  unit,
}) => {
  const onUp = () => {
    onInputChange(parseFloat(value) + step);
  };

  const onDown = () => {
    onInputChange(parseFloat(value) - step);
  };

  const onInputChange = (v) => {
    if (!v || isNaN(parseFloat(v)) || parseFloat(v) <= 0) {
      onChange("0");
      return;
    }
    let num = parseFloat(v);
    if (num >= max) {
      num = max;
    } else if (num <= min) {
      num = min;
    }
    onChange(num);
  };

  return (
    <Grid container alignItems="center">
      <TransparentTextInput
        textAlign="right"
        value={value}
        onChange={onInputChange}
        min={min}
        max={max}
        width={inputWidth || 30}
      />
      {unit && <Typography>{unit}</Typography>}
      <Grid container direction="column" style={{ width: "fit-content", marginLeft: 8 }}>
        <FaCaretUp onClick={onUp} />
        <FaCaretDown onClick={onDown} />
      </Grid>
    </Grid>
  );
};
